<template>
  <div class="container-fluid p-3 p-md-4">
    <div class="d-flex justify-content-between align-items-start gap-2 mb-4 flex-wrap">
      <div>
        <h2 class="mb-0">
          <i :class="[NAV_ITEMS.WatchlistHistory.icon, NAV_ITEMS.WatchlistHistory.colorClass, 'me-2']"></i>Watchlist History
        </h2>
        <small class="text-muted">Every watchlist the researcher built, by session, with the evidence behind each entry</small>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap justify-content-end">
        <router-link to="/watchlist" class="btn btn-sm btn-outline-secondary">
          <i class="fas fa-binoculars me-1"></i> Today's watchlist
        </router-link>
        <button class="btn btn-sm btn-outline-primary" :disabled="!entries.length || downloading" @click="downloadCsv">
          <i class="fas fa-file-csv me-1"></i> {{ downloading ? 'Preparing…' : 'Download CSV' }}
        </button>
      </div>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="form-label small text-muted mb-1" for="history-date">Session</label>
          <div class="input-group input-group-sm">
            <button class="btn btn-outline-secondary" :disabled="!olderDate" title="Previous session" @click="selectDate(olderDate)">
              <i class="fas fa-chevron-left"></i>
            </button>
            <select id="history-date" v-model="selectedDate" class="form-select" @change="loadForDate">
              <option v-if="!dates.length" value="">No history yet</option>
              <option v-for="d in dates" :key="d.session_date" :value="d.session_date">
                {{ d.session_date }} — {{ d.ticker_count }} tickers ({{ d.up_count }}↑ {{ d.down_count }}↓)
              </option>
            </select>
            <button class="btn btn-outline-secondary" :disabled="!newerDate" title="Next session" @click="selectDate(newerDate)">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
        <form class="d-flex gap-2 align-items-end" @submit.prevent="loadForTicker">
          <div>
            <label class="form-label small text-muted mb-1" for="history-ticker">Ticker across sessions</label>
            <input id="history-ticker" v-model="tickerQuery" type="search" class="form-control form-control-sm"
                   placeholder="e.g. AAPL" maxlength="10" />
          </div>
          <button class="btn btn-sm btn-primary" type="submit" :disabled="!tickerQuery.trim()">Look up</button>
          <button v-if="mode === 'ticker'" class="btn btn-sm btn-link" type="button" @click="clearTicker">Back to sessions</button>
        </form>
        <div class="ms-auto small text-muted" v-if="entries.length">
          {{ uniqueTickers }} tickers · {{ upCount }} bullish · {{ downCount }} bearish
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"><span class="visually-hidden">Loading...</span></div>
    </div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
    <div v-else-if="!entries.length" class="alert alert-info">
      <i class="fas fa-info-circle me-2"></i>
      <template v-if="mode === 'ticker'">{{ activeTicker }} has not been on a recorded watchlist.</template>
      <template v-else>No recorded watchlist yet. History starts with the first build after this feature was deployed.</template>
    </div>

    <div v-else class="card shadow-sm">
      <div class="card-body p-0">
        <div class="table-responsive">
          <table class="table table-hover mb-0 align-middle">
            <thead class="table-light">
              <tr>
                <th v-if="mode === 'ticker'">Session</th>
                <th>Ticker</th>
                <th>Setup</th>
                <th>Direction</th>
                <th class="text-end">Conf.</th>
                <th>Signal</th>
                <th class="text-end">Close</th>
                <th class="text-end">Support</th>
                <th class="text-end">Resistance</th>
                <th class="text-end">Risk / Reward</th>
                <th>News</th>
                <th>Index</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(entry, i) in entries" :key="rowKey(entry, i)">
                <tr class="history-row" @click="toggle(rowKey(entry, i))">
                  <td v-if="mode === 'ticker'" class="text-nowrap">{{ entry.session_date }}</td>
                  <td class="fw-bold">{{ entry.ticker }}</td>
                  <td>
                    <span class="badge" :class="getQualifierMeta(entry.qualifier_type).badgeClass">
                      {{ getQualifierMeta(entry.qualifier_type).label }}
                    </span>
                    <span v-if="entry.source === 'merge'" class="badge bg-light text-dark border ms-1" title="Added by a user suggestion">merge</span>
                  </td>
                  <td>
                    <span v-if="getDirectionMeta(entry.direction)" class="badge" :class="getDirectionMeta(entry.direction).badgeClass">
                      {{ getDirectionMeta(entry.direction).label }}
                    </span>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td class="text-end">{{ entry.confidence ?? '—' }}</td>
                  <td class="text-nowrap">{{ signalText(entry) }}</td>
                  <td class="text-end">{{ money(entry.last_close) }}</td>
                  <td class="text-end" :title="levelTitle(a(entry).support)">{{ levelText(a(entry).support) }}</td>
                  <td class="text-end" :title="levelTitle(a(entry).resistance)">{{ levelText(a(entry).resistance) }}</td>
                  <td class="text-end text-nowrap">{{ riskRewardText(entry) }}</td>
                  <td class="text-nowrap">{{ newsText(entry) }}</td>
                  <td><span class="badge bg-light text-dark border">{{ entry.index }}</span></td>
                  <td class="text-muted"><i class="fas" :class="expanded[rowKey(entry, i)] ? 'fa-chevron-up' : 'fa-chevron-down'"></i></td>
                </tr>
                <tr v-if="expanded[rowKey(entry, i)]" class="bg-light">
                  <td :colspan="mode === 'ticker' ? 13 : 12">
                    <div class="small mb-2" style="white-space: pre-wrap; word-break: break-word;">{{ entry.reason || '—' }}</div>
                    <dl v-if="analysisPairs(entry).length" class="row small mb-0">
                      <template v-for="[key, value] in analysisPairs(entry)" :key="key">
                        <dt class="col-sm-3 col-lg-2 text-muted fw-normal">{{ key }}</dt>
                        <dd class="col-sm-9 col-lg-4 mb-1 text-break">{{ value }}</dd>
                      </template>
                    </dl>
                    <div v-else class="small text-muted">No analysis recorded for this entry.</div>
                    <div class="small text-muted mt-2">Recorded {{ entry.recorded_at || '—' }} · source {{ entry.source }}</div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchWatchlistHistoryDates, fetchWatchlistHistory, downloadWatchlistHistoryCsv } from '../services/api'
import { NAV_ITEMS } from '../constants/navItems'
import { getQualifierMeta, getDirectionMeta } from '../constants/watchlistQualifiers'

const LOG_PREFIX = '[WatchlistHistory]'

// Analysis fields already shown as table columns (or plumbing), left out of the detail list.
const HIDDEN_ANALYSIS_KEYS = new Set(['support', 'resistance', 'strategy'])

export default {
  name: 'WatchlistHistory',
  data() {
    return {
      dates: [],
      selectedDate: '',
      tickerQuery: '',
      activeTicker: null,
      mode: 'date',
      entries: [],
      expanded: {},
      loading: true,
      downloading: false,
      error: null,
      NAV_ITEMS,
    }
  },
  computed: {
    dateIndex() {
      return this.dates.findIndex(d => d.session_date === this.selectedDate)
    },
    olderDate() {
      const i = this.dateIndex
      return i >= 0 && i + 1 < this.dates.length ? this.dates[i + 1].session_date : null
    },
    newerDate() {
      const i = this.dateIndex
      return i > 0 ? this.dates[i - 1].session_date : null
    },
    uniqueTickers() {
      return new Set(this.entries.map(e => e.ticker)).size
    },
    upCount() {
      return new Set(this.entries.filter(e => e.direction === 'up').map(e => `${e.session_date}-${e.ticker}`)).size
    },
    downCount() {
      return new Set(this.entries.filter(e => e.direction === 'down').map(e => `${e.session_date}-${e.ticker}`)).size
    },
  },
  async mounted() {
    await this.loadDates()
  },
  methods: {
    getQualifierMeta,
    getDirectionMeta,
    async loadDates() {
      this.loading = true
      this.error = null
      try {
        const data = await fetchWatchlistHistoryDates(365)
        this.dates = Array.isArray(data?.dates) ? data.dates : []
        const wanted = this.$route.query.date
        this.selectedDate = this.dates.some(d => d.session_date === wanted)
          ? wanted
          : (this.dates[0]?.session_date || '')
        if (this.$route.query.ticker) {
          this.tickerQuery = String(this.$route.query.ticker)
          await this.loadForTicker()
        } else if (this.selectedDate) {
          await this.loadForDate()
        } else {
          this.entries = []
          this.loading = false
        }
      } catch (err) {
        this.fail(err)
      }
    },
    selectDate(date) {
      if (!date) return
      this.selectedDate = date
      this.loadForDate()
    },
    async loadForDate() {
      this.mode = 'date'
      this.activeTicker = null
      await this.load({ date: this.selectedDate })
      this.syncQuery({ date: this.selectedDate })
    },
    async loadForTicker() {
      const ticker = this.tickerQuery.trim().toUpperCase()
      if (!ticker) return
      this.mode = 'ticker'
      this.activeTicker = ticker
      await this.load({ ticker })
      this.syncQuery({ ticker })
    },
    clearTicker() {
      this.tickerQuery = ''
      this.loadForDate()
    },
    async load(filters) {
      this.loading = true
      this.error = null
      this.expanded = {}
      try {
        const data = await fetchWatchlistHistory(filters)
        this.entries = Array.isArray(data?.entries) ? data.entries : []
      } catch (err) {
        this.fail(err)
      } finally {
        this.loading = false
      }
    },
    fail(err) {
      this.error = err.response?.data?.detail || err.message || 'Failed to load watchlist history'
      console.error(`${LOG_PREFIX} ${this.error}`)
      this.loading = false
    },
    syncQuery(query) {
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query }).catch(() => {})
      }
    },
    async downloadCsv() {
      this.downloading = true
      try {
        await downloadWatchlistHistoryCsv(this.mode === 'ticker' ? { ticker: this.activeTicker } : { date: this.selectedDate })
      } catch (err) {
        this.fail(err)
      } finally {
        this.downloading = false
      }
    },
    rowKey(entry, i) {
      return `${entry.session_date}-${entry.index}-${entry.ticker}-${i}`
    },
    toggle(key) {
      this.expanded = { ...this.expanded, [key]: !this.expanded[key] }
    },
    a(entry) {
      return entry.analysis && typeof entry.analysis === 'object' ? entry.analysis : {}
    },
    money(value) {
      return value === null || value === undefined ? '—' : Number(value).toFixed(2)
    },
    signalText(entry) {
      const an = this.a(entry)
      if (an.signal) {
        const age = an.signal_age === 0 ? 'T' : `T-${an.signal_age}`
        return `${an.signal} · ${age} · ${an.signal_status}`
      }
      if (an.extreme) return `range ${an.extreme} · ${an.continued ?? 0}/${an.reversed ?? 0}/${an.flat ?? 0}`
      return '—'
    },
    levelText(level) {
      return level && level.price !== undefined ? Number(level.price).toFixed(2) : '—'
    },
    levelTitle(level) {
      if (!level) return ''
      const zone = `${Number(level.zone_low).toFixed(2)}–${Number(level.zone_high).toFixed(2)}`
      return `${level.pattern || level.source} (zone ${zone})`
    },
    riskRewardText(entry) {
      const an = this.a(entry)
      const risk = an.risk_per_share
      const reward = an.reward_per_share
      if (risk === undefined && reward === undefined) return '—'
      const fmt = v => (v === null || v === undefined ? 'n/a' : `$${Number(v).toFixed(2)}`)
      const rr = an.reward_risk_ratio
      return `${fmt(risk)} / ${fmt(reward)}${rr ? ` (${Number(rr).toFixed(1)}R)` : ''}`
    },
    newsText(entry) {
      const an = this.a(entry)
      if (!an.news_effect) return '—'
      const verdict = an.news_direction ? ` ${an.news_direction} ${an.news_confidence ?? ''}`.trimEnd() : ''
      return `${an.news_effect}${verdict}`
    },
    analysisPairs(entry) {
      return Object.entries(this.a(entry))
        .filter(([key, value]) => !HIDDEN_ANALYSIS_KEYS.has(key) && value !== null && value !== undefined)
        .map(([key, value]) => [key, typeof value === 'object' ? JSON.stringify(value) : String(value)])
    },
  },
}
</script>

<style scoped>
.history-row {
  cursor: pointer;
}
</style>
